// frontend/src/pages/AuditLogs.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getAuditLogs, getAuditActions } from "../api/audit";

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [userEmail, setUserEmail] = useState("");
  const [action, setAction] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [limit, setLimit] = useState(100);

  // Load available action types once
  useEffect(() => {
    getAuditActions()
      .then((data) => setActions(data.actions || []))
      .catch(() => {});
  }, []);

  // Load logs on mount
  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchLogs() {
    setLoading(true);
    setError("");
    try {
      const data = await getAuditLogs({ userEmail, action, startDate, endDate, limit });
      setLogs(data.logs || []);
    } catch (err) {
      setError(err?.message || "Failed to load audit logs");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    fetchLogs();
  }

  const successCounts = useMemo(() => {
    const ok = logs.filter((l) => l.success !== false).length;
    const fail = logs.length - ok;
    return { ok, fail };
  }, [logs]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0" style={{ fontWeight: 500 }}>Audit Logs</h5>
        {logs.length > 0 && (
          <span className="text-muted small">
            {logs.length} entries &mdash; {successCounts.ok} success / {successCounts.fail} failed
          </span>
        )}
      </div>

      {/* Filter bar */}
      <form onSubmit={handleSubmit}>
        <div className="card shadow-sm mb-3" style={{ borderRadius: "8px" }}>
          <div className="card-body py-3">
            <div className="row g-2 align-items-end">
              <div className="col-12 col-sm-6 col-lg-3">
                <label className="form-label mb-1 small">User email</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Partial match..."
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                />
              </div>

              <div className="col-12 col-sm-6 col-lg-2">
                <label className="form-label mb-1 small">Action</label>
                <select
                  className="form-select form-select-sm"
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                >
                  <option value="">All actions</option>
                  {actions.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-lg-2">
                <label className="form-label mb-1 small">From</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="col-6 col-lg-2">
                <label className="form-label mb-1 small">To</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div className="col-6 col-lg-1">
                <label className="form-label mb-1 small">Limit</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  min={1}
                  max={1000}
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                />
              </div>

              <div className="col-6 col-lg-2 d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-dark btn-sm w-100"
                  disabled={loading}
                >
                  {loading ? "Loading…" : "Apply"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    setUserEmail("");
                    setAction("");
                    setStartDate("");
                    setEndDate("");
                    setLimit(100);
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      {error && (
        <div className="alert alert-danger py-2" role="alert">{error}</div>
      )}

      {/* Logs table */}
      <div className="card shadow-sm" style={{ borderRadius: "8px" }}>
        <div className="card-body p-0">
          {loading ? (
            <div className="p-3 text-muted">Loading audit logs…</div>
          ) : logs.length === 0 ? (
            <div className="p-3 text-muted">No audit log entries found.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0" style={{ fontSize: "0.85rem" }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ whiteSpace: "nowrap" }}>Timestamp</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Resource</th>
                    <th>Org ID</th>
                    <th>IP</th>
                    <th className="text-center">Status</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <AuditRow key={log.id} log={log} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AuditRow({ log }) {
  const [expanded, setExpanded] = useState(false);
  const ts = log.timestamp
    ? new Date(log.timestamp).toLocaleString()
    : "—";

  let parsedDetails = null;
  if (log.details) {
    try {
      parsedDetails = typeof log.details === "string"
        ? JSON.parse(log.details)
        : log.details;
    } catch {
      parsedDetails = log.details;
    }
  }

  return (
    <>
      <tr className={log.success === false ? "table-danger" : ""}>
        <td style={{ whiteSpace: "nowrap" }}>{ts}</td>
        <td className="text-truncate" style={{ maxWidth: "180px" }} title={log.user_email}>
          {log.user_email}
        </td>
        <td>
          <ActionBadge action={log.action} />
        </td>
        <td>{log.resource || "—"}</td>
        <td>{log.organization_id ?? "—"}</td>
        <td>{log.ip_address || "—"}</td>
        <td className="text-center">
          {log.success === false ? (
            <span title={log.error_message || ""}>❌</span>
          ) : (
            <span>✅</span>
          )}
        </td>
        <td>
          {parsedDetails ? (
            <button
              className="btn btn-link btn-sm p-0"
              onClick={() => setExpanded((x) => !x)}
            >
              {expanded ? "Hide" : "View"}
            </button>
          ) : "—"}
        </td>
      </tr>
      {expanded && parsedDetails && (
        <tr>
          <td colSpan={8} className="bg-light">
            <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
              {JSON.stringify(parsedDetails, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

const ACTION_COLORS = {
  login: "success",
  logout: "secondary",
  feature_flag_update: "primary",
  matrix_update: "primary",
  admin_user_ensure: "info",
  organization_create: "warning",
  auditr_role_change: "danger",
};

function ActionBadge({ action }) {
  const color = ACTION_COLORS[action] || "secondary";
  return (
    <span className={`badge bg-${color}`} style={{ fontSize: "0.75em" }}>
      {action}
    </span>
  );
}
